<template>
  <div v-if="this.allSections.length">
    <!-- options -->
    <div>
      <BaseButton
        :text="`Back to 'Your quotes'`"
        flat
        ripple
        class="font-weight-bold d-flex ml-n5"
        icon="mdi-chevron-left"
        color="#106175"
        small
        @click="$router.push({ name: 'YourQuotesPage' })"
        aria-label="Back to 'Your quotes'"
      />

      <div class="d-flex justify-space-between mt-10">
        <h1 class="font-24 font-lg-28 font-weight-bold primary--text">
          Your application
        </h1>
        <BaseButton
          :text="$vuetify.breakpoint.lgAndUp ? 'Save &amp; exit' : 'Save'"
          flat
          depressed
          ripple
          class="font-weight-bold px-1 py-3"
          icon="mdi-content-save"
          color="#106175"
          small
          @click="toggleGlobalSaveAndExitDialog"
          aria-label="Save and exit"
        />
      </div>
    </div>

    <!-- the form -->
    <ValidationObserver ref="yourApplicationForm" v-slot="{ validate }">
      <form @submit.prevent="onSubmit(validate)">
        <h2 class="my-5 font-20 font-weight-bold primary--text">
          Policy details
        </h2>

        <BaseLabel text="Basis of Advice" class="mt-n2 mb-2" />
        <BaseSelect
          v-model="basisOfAdvice.Value"
          dataUiid="BasisOfAdvice"
          :items="basisOfAdviceItems"
          :rules="{
            required: {
              required: true,
              fieldLabel: 'Basis of advice',
            },
          }"
          :style="{
            'max-width': $vuetify.breakpoint.lgAndUp ? '440px' : '100%',
          }"
          :height="$vuetify.breakpoint.lgAndUp ? '48' : '60'"
          aria-label="Basis of Advice"
        />

        <BaseLabel text="Policy start date (optional)" class="mt-10 mb-2" />
        <BaseInputDate
          v-model="policyStartDate.Value"
          dataUiid="PolicyStartDate"
          :rules="policyStartDateRule"
          :style="{
            'max-width': $vuetify.breakpoint.lgAndUp ? '440px' : '100%',
          }"
          :height="$vuetify.breakpoint.lgAndUp ? '48' : '60'"
        />

        <BaseLabel text="Document delivery preferences" class="mt-2 mb-2" />
        <BaseToggleButton
          v-model="documentDeliveryPreferenceLocal"
          :toggleButtonText="{
            yes: 'Online',
            no: 'Post',
            yesDataUiid: 'DocumentDeliveryPreference_Online',
            noDataUiid: 'DocumentDeliveryPreference_Postal',
          }"
          :height="$vuetify.breakpoint.lgAndUp ? '48' : '60'"
          :min-width="$vuetify.breakpoint.lgAndUp ? '110' : ''"
        />

        <h2 class="mt-10 mb-5 font-20 font-weight-bold primary--text">
          Applicant details
        </h2>

        <BaseLabel text="Phone number" class="mt-n2 mb-2" />
        <BaseInput
          v-model="applicant1TelNo.Value"
          dataUiid="Applicant1TelNo"
          hideDetails="auto"
          :rules="{
            required: {
              required: true,
              fieldLabel: 'Phone number',
            },
            phone_number: {
              fieldLabel: 'Phone number',
            },
          }"
          :style="{
            'max-width': $vuetify.breakpoint.lgAndUp ? '440px' : '100%',
          }"
          :height="$vuetify.breakpoint.lgAndUp ? '48' : '60'"
          aria-label="Phone number"
        />

        <BaseLabel text="Email address" class="mt-10 mb-2" />
        <BaseInput
          v-model="applicant1EmailAddress.Value"
          dataUiid="Applicant1EmailAddress"
          hideDetails="auto"
          :rules="{
            required: {
              required: true,
              fieldLabel: 'Email address',
            },
            regex: {
              pattern:
                '^[\\w\\-\\.\\+]+\\@[a-zA-Z0-9\\.\\-]+\\.[a-zA-z0-9]{2,}$',
              customMessage: 'Please enter a valid email address.',
            },
            max: {
              length: 50,
              errorMessageTemplate:
                'Email address may not be greater than 50 characters.',
            },
          }"
          :style="{
            'max-width': $vuetify.breakpoint.lgAndUp ? '440px' : '100%',
          }"
          :height="$vuetify.breakpoint.lgAndUp ? '48' : '60'"
          aria-label="Email address"
        />

        <BaseLabel
          v-if="!isShowCorrespondenceAddressLabel"
          text="Correspondence address"
          class="mt-10 mb-2"
        />

        <BaseAlert
          color="var(--blue-100)"
          class="pa-4 mt-4 border-sm border-solid border-info"
          :style="{
            'max-width': '658px',
          }"
        >
          <div class="d-flex flex-no-wrap">
            <div class="mr-3">
              <v-icon color="var(--blue-500)">mdi-alert-circle</v-icon>
            </div>
            <div class="primary--text">
              <span class="font-weight-bold">Note!</span> To be eligible for
              this policy, You (the person named on the policy certificate) must
              permanently reside in the United Kingdom. Or, if insured in the
              name of a limited company, it must be registered with Companies
              House in the United Kingdom.
            </div>
          </div>
        </BaseAlert>

        <BaseAddressLookup
          dataUiid="CorrespondenceAddress_Find_Property"
          showManualAddressForm
          manualAddressFormText="Correspondence address"
          :placeholder="
            $vuetify.breakpoint.lgAndUp
              ? 'Start typing your address or postcode...'
              : 'Start typing...'
          "
          manualFormUiid="CorrespondenceAddress"
          id="address-lookup-correspondence"
          @address-selected="correspondenceAddressSelected"
          @is-address-selected="correspondenceAddressLabel"
          @use-address-finder="correspondenceUseAddressFinder"
          :address="correspondenceAddress"
          :rules="{
            required: {
              required: true,
              fieldLabel: 'Correspondence address',
            },
          }"
          :style="{
            'max-width': $vuetify.breakpoint.lgAndUp ? '440px' : '100%',
          }"
          :height="$vuetify.breakpoint.lgAndUp ? '48' : '60'"
        />

        <!-- second applicant contact details -->
        <template v-if="isSecondApplicantIncluded">
          <h2 class="mt-10 mb-5 font-20 font-weight-bold primary--text">
            Second applicant details
          </h2>

          <BaseLabel text="Phone number" class="mt-n2 mb-2" />
          <BaseInput
            v-model="applicant2TelNo.Value"
            dataUiid="Applicant2TelNo"
            hideDetails="auto"
            :rules="{
              required: {
                required: true,
                fieldLabel: 'Phone number',
              },
              phone_number: {
                fieldLabel: 'Phone number',
              },
            }"
            :style="{
              'max-width': $vuetify.breakpoint.lgAndUp ? '440px' : '100%',
            }"
            :height="$vuetify.breakpoint.lgAndUp ? '48' : '60'"
            aria-label="Phone number"
          />

          <BaseLabel text="Email address" class="mt-10 mb-2" />
          <BaseInput
            v-model="applicant2EmailAddress.Value"
            dataUiid="Applicant2EmailAddress"
            hideDetails="auto"
            :rules="{
              required: {
                required: true,
                fieldLabel: 'Email address',
              },
              regex: {
                pattern:
                  '^[\\w\\-\\.\\+]+\\@[a-zA-Z0-9\\.\\-]+\\.[a-zA-z0-9]{2,}$',
                customMessage: 'Please enter a valid email address.',
              },
              max: {
                length: 50,
                errorMessageTemplate:
                  'Email address may not be greater than 50 characters.',
              },
            }"
            :style="{
              'max-width': $vuetify.breakpoint.lgAndUp ? '440px' : '100%',
            }"
            :height="$vuetify.breakpoint.lgAndUp ? '48' : '60'"
            aria-label="Email address"
          />
        </template>

        <h2 class="mt-10 mb-5 font-20 font-weight-bold primary--text">
          Mortgage details (optional)
        </h2>

        <BaseLabel text="Mortgage lender" class="mt-n2 mb-2" />
        <BaseInput
          v-model="mortgageLender.Value"
          dataUiid="MortgageLender"
          hideDetails="auto"
          :rules="{
            max: {
              length: 30,
              errorMessageTemplate:
                'Mortgage Lender may not be greater than 30 characters.',
            },
          }"
          :style="{
            'max-width': $vuetify.breakpoint.lgAndUp ? '440px' : '100%',
          }"
          :height="$vuetify.breakpoint.lgAndUp ? '48' : '60'"
          aria-label="Mortgage lender"
        />

        <BaseLabel text="Mortgage completion date" class="mt-10 mb-2" />
        <BaseInputDate
          v-model="mortgageStartDate.Value"
          dataUiid="MortgageStartDate"
          :rules="{
            date_valid: {
              fieldLabel: 'Mortgage completion date',
              errorMessageTemplate: 'Mortgage completion date is not valid.',
            },
            regex: {
              pattern: '^\\s*$|^(\\d{2})\\/(\\d{2})\\/\\d{4}$',
              customMessage: 'Mortgage completion date is not valid.',
            },
            date_in_range: {
              min: 35,
              max: 1,
              units: 'years',
              customMessage:
                'Mortgage completion date must be less than 35 years in the past and be no more than 1 year into the future.',
            },
          }"
          :style="{
            'max-width': $vuetify.breakpoint.lgAndUp ? '440px' : '100%',
          }"
          :height="$vuetify.breakpoint.lgAndUp ? '48' : '60'"
        />

        <h2 class="mt-3 mb-5 font-20 font-weight-bold primary--text">
          Solicitor details (optional)
        </h2>

        <BaseLabel text="Solicitor name" class="mt-n2 mb-2" />
        <BaseInput
          v-model="solicitorName.Value"
          dataUiid="SolicitorName"
          hideDetails="auto"
          :rules="{
            regex: {
              pattern: '^[a-zA-Z][a-zA-Z-\'\\s]*$',
              customMessage:
                'First character must be a letter. Only letters, spaces, hyphens, and apostrophes are allowed in this field.',
            },
            max: {
              length: 30,
              fieldLabel: 'Solicitor name',
            },
          }"
          :style="{
            'max-width': $vuetify.breakpoint.lgAndUp ? '440px' : '100%',
          }"
          :height="$vuetify.breakpoint.lgAndUp ? '48' : '60'"
          aria-label="Solicitor name"
        />

        <BaseLabel
          v-if="!isShowSolicitorAddressLabel"
          text="Solicitor address"
          class="mt-10 mb-2"
        />

        <BaseAddressLookup
          dataUiid="SolicitorAddress_Find_Property"
          manualFormUiid="Solicitor"
          showManualAddressForm
          manualAddressFormText="Solicitor address"
          :placeholder="
            $vuetify.breakpoint.lgAndUp
              ? 'Start typing your address or postcode...'
              : 'Start typing...'
          "
          @address-selected="solicitorAddressSelected"
          @use-address-finder="solicitorUseAddressFinder"
          @is-address-selected="solicitorAddressLabel"
          id="address-lookup-solicitor"
          :address="solicitorAddress"
          :style="{
            'max-width': $vuetify.breakpoint.lgAndUp ? '440px' : '100%',
          }"
          :height="$vuetify.breakpoint.lgAndUp ? '48' : '60'"
        />

        <BaseLabel text="Email intention to solicitor?" class="mt-10 mb-2" />
        <BaseToggleButton
          v-model="emailAcceptToSolicitor.Value"
          :toggleButtonText="{
            yesDataUiid: 'EmailAcceptToSolicitor_yes',
            noDataUiid: 'EmailAcceptToSolicitor_no',
          }"
          :height="$vuetify.breakpoint.lgAndUp ? '48' : '60'"
          :min-width="$vuetify.breakpoint.lgAndUp ? '110' : ''"
        />

        <BaseLabel
          text="Email risk notification to solicitor?"
          class="mt-10 mb-2"
        />
        <BaseToggleButton
          v-model="emailNotificationToSolicitor.Value"
          :toggleButtonText="{
            yesDataUiid: 'EmailNotificationToSolicitor_yes',
            noDataUiid: 'EmailNotificationToSolicitor_no',
          }"
          :height="$vuetify.breakpoint.lgAndUp ? '48' : '60'"
          :min-width="$vuetify.breakpoint.lgAndUp ? '110' : ''"
        />

        <!-- solicitor email address -->
        <template
          v-if="
            emailAcceptToSolicitor.Value || emailNotificationToSolicitor.Value
          "
        >
          <BaseLabel text="Solicitor email address" class="mt-10 mb-2" />
          <BaseInput
            v-model="solicitorEmailAddress.Value"
            dataUiid="SolicitorEmailAddress"
            hideDetails="auto"
            :rules="{
              required: {
                required: true,
                fieldLabel: 'Solicitor email address',
              },
              regex: {
                pattern:
                  '^[\\w\\-\\.\\+]+\\@[a-zA-Z0-9\\.\\-]+\\.[a-zA-z0-9]{2,}$',
                customMessage: 'Please enter a valid email address.',
              },
              max: {
                length: 50,
                errorMessageTemplate:
                  'Email address may not be greater than 50 characters.',
              },
            }"
            :style="{
              'max-width': $vuetify.breakpoint.lgAndUp ? '440px' : '100%',
            }"
            :height="$vuetify.breakpoint.lgAndUp ? '48' : '60'"
            aria-label="Solicitor email address"
          />
        </template>

        <BaseLabel text="Your reference (optional)" class="mt-10 mb-2" />
        <BaseInput
          v-model="yourRef.Value"
          dataUiid="YourRef"
          hideDetails="auto"
          :rules="{
            max: {
              length: 40,
              fieldLabel: 'Your reference (optional)',
            },
            regex: {
              pattern: USER_REF_EXCLUDE_ANGLE_BRACKETS_REGEX,
              customMessage: ERROR_MSG_USER_REF,
            },
          }"
          :style="{
            'max-width': $vuetify.breakpoint.lgAndUp ? '440px' : '100%',
          }"
          :height="$vuetify.breakpoint.lgAndUp ? '48' : '60'"
          aria-label="Your reference (optional)"
        />

        <BaseLabel
          text="Additional information (optional)"
          class="mt-10 mb-2"
        />
        <BaseInput
          v-model="additionalInfo.Value"
          dataUiid="AdditionalInfo"
          hideDetails="auto"
          :rules="{
            max: {
              length: 200,
              fieldLabel: 'Additional information (optional)',
            },
            regex: {
              pattern: '^[a-zA-Z0-9\\s\-\']*$',
              customMessage:
                'Please enter only alphanumeric characters, spaces, or any of the following characters in this field - \'',
            },
          }"
          :style="{
            'max-width': $vuetify.breakpoint.lgAndUp ? '440px' : '100%',
          }"
          :height="$vuetify.breakpoint.lgAndUp ? '48' : '60'"
          aria-label="Additional information (optional)"
        />

        <template v-if="isAdvisedSeller">
          <BaseAlert
            color="var(--blue-100)"
            class="pa-4 mt-10 border-sm border-solid border-info"
          >
            <div class="d-flex flex-no-wrap">
              <div class="mr-3">
                <v-icon color="var(--blue-500)">mdi-information</v-icon>
              </div>
              <div class="primary--text">
                <span class="font-weight-bold">Important!</span> Before you
                provide payment details, please confirm that you are happy to
                proceed. You have only received limited information about
                Paymentshield, our services and policies. Full details,
                including terms and conditions, benefits, limitations and
                exclusions will be sent to you via your chosen preference in the
                next couple of days.
              </div>
            </div>
          </BaseAlert>

          <BaseCheckBox
            className="mt-3 font-weight-bold primary--text"
            label="I confirm that I have read the above statement and I am happy to proceed."
            :hideDetails="false"
            :checked="limitedInformationStatement.Value"
            :rules="{
              required: {
                required: true,
                fieldLabel: 'Confirmation',
              },
            }"
            @change="(val) => (limitedInformationStatement.Value = val)"
            :vid="limitedInformationStatement.InterfaceKey"
            :dataUiid="limitedInformationStatement.InterfaceKey"
            aria-label="I confirm that I have read the above statement and I am happy to proceed."
          />
        </template>

        <BaseButton
          depressed
          rounded
          dense
          class="font-weight-bold font-20 pa-7 px-13 mt-5 mb-3"
          type="submit"
          :color="'psorange'"
          :block="$vuetify.breakpoint.lgAndUp ? false : true"
          :text="
            $vuetify.breakpoint.lgAndUp ? 'CONTINUE TO PAYMENT' : 'CONTINUE'
          "
          aria-label="Continue to payment"
        />
      </form>
    </ValidationObserver>
  </div>
</template>

<script>
import {
  addFieldLabel,
  convertToBool,
  scrollTop,
  tryGoToNextStep,
} from "@/common/utils";
import { DOC_DELIVERY_PREF } from "@/constants/common.constants";
import {
  BASIS_OF_ADVICE,
  setItemDataUiids,
} from "@/constants/component.constants.js";
import {
  ERROR_MSG_USER_REF,
  ERROR_INVALID,
} from "@/constants/message.constants";
import {
  DATE_REGEX,
  USER_REF_EXCLUDE_ANGLE_BRACKETS_REGEX,
} from "@/constants/regex-patterns.constants";
import { ROUTE_NAME } from "@/constants/route.constants";
import {
  APPLICATION_MODULE,
  QUESTIONS_MODULE,
} from "@/store/types/module-types";
import { ValidationObserver } from "vee-validate";
import { mapActions, mapGetters, mapState } from "vuex";

import BaseAddressLookup from "@/components/BaseComponents/BaseAddressLookup";
import BaseAlert from "@/components/BaseComponents/BaseAlert";
import BaseButton from "@/components/BaseComponents/BaseButton";
import BaseInput from "@/components/BaseComponents/BaseInput";
import BaseInputDate from "@/components/BaseComponents/BaseInputDate";
import BaseLabel from "@/components/BaseComponents/BaseLabel";
import BaseSelect from "@/components/BaseComponents/BaseSelect";
import BaseToggleButton from "@/components/BaseComponents/BaseToggleButton";
import BaseCheckBox from "@/components/BaseComponents/BaseCheckBox.vue";
import { formatAddress } from "@/common/addresses";

export default {
  name: "YourApplicationPage",
  components: {
    ValidationObserver,
    BaseButton,
    BaseLabel,
    BaseSelect,
    BaseInputDate,
    BaseToggleButton,
    BaseInput,
    BaseAddressLookup,
    BaseAlert,
    BaseCheckBox,
  },
  data() {
    return {
      setItemDataUiids,
      BASIS_OF_ADVICE,
      APPLICATION_MODULE,
      USER_REF_EXCLUDE_ANGLE_BRACKETS_REGEX,
      ERROR_MSG_USER_REF,

      basisOfAdvice: null,
      applicant1Title: null,
      policyStartDate: null,
      documentDeliveryPreference: null,
      documentDeliveryPreferenceLocal: true,
      applicant1TelNo: null,
      applicant1EmailAddress: null,

      // correspondence address
      correspondenceAddress: {},
      isShowCorrespondenceAddressLabel: false,

      applicant2TelNo: null,
      applicant2EmailAddress: null,

      // solicitor address
      solicitorAddress: {},
      isShowSolicitorAddressLabel: false,

      mortgageLender: null,
      mortgageStartDate: null,
      solicitorName: null,
      emailAcceptToSolicitor: null,
      emailNotificationToSolicitor: null,
      yourRef: null,
      additionalInfo: null,
      solicitorEmailAddress: null,

      limitedInformationStatement: {},

      // adviser overrides
      BASIS_OF_ADVICE_ADVISED: [
        {
          text: "Advised",
          value: "Advised",
        },
      ],
    };
  },
  watch: {
    documentDeliveryPreferenceLocal(val) {
      if (val) {
        this.documentDeliveryPreference.Value = DOC_DELIVERY_PREF.Online;
      } else {
        this.documentDeliveryPreference.Value = DOC_DELIVERY_PREF.Postal;
      }
    },
  },
  computed: {
    ...mapState(QUESTIONS_MODULE, ["restoringLastStep"]),
    ...mapState(APPLICATION_MODULE, ["questionMap"]),
    ...mapGetters(QUESTIONS_MODULE, ["getBoolAnswer", "isAdvisedSeller"]),
    ...mapGetters(APPLICATION_MODULE, ["allSections", "selectedQuote"]),
    isSecondApplicantIncluded() {
      return this.getBoolAnswer("SecondApplicantIncluded");
    },
    basisOfAdviceItems() {
      return !this.isAdvisedSeller
        ? setItemDataUiids("BasisOfAdvice", BASIS_OF_ADVICE)
        : setItemDataUiids("BasisOfAdvice", this.BASIS_OF_ADVICE_ADVISED);
    },
    policyStartDateRule() {
      return addFieldLabel(
        {
          date_valid: {
            errorMessageTemplate: ERROR_INVALID,
          },
          regex: {
            errorMessageTemplate: ERROR_INVALID,
            pattern: DATE_REGEX,
          },
          between_now_and: {
            future: this.selectedQuote?.QuoteExpiryDate,
          },
        },
        "Policy start date"
      );
    },
  },
  created() {
    if (this.allSections.length) {
      this.basisOfAdvice = this.questionMap.BasisOfAdvice;

      this.policyStartDate = this.questionMap.PolicyStartDate;
      this.documentDeliveryPreference =
        this.questionMap.DocumentDeliveryPreference;
      this.setInitialDocDeliveryPref();
      this.applicant1TelNo = this.questionMap.Applicant1TelNo;
      this.applicant1EmailAddress = this.questionMap.Applicant1EmailAddress;

      // correspondence address
      this.correspondenceAddress.Street = this.questionMap.CorrespondenceStreet;
      this.correspondenceAddress.District =
        this.questionMap.CorrespondenceDistrict;
      this.correspondenceAddress.Town = this.questionMap.CorrespondenceTown;
      this.correspondenceAddress.County = this.questionMap.CorrespondenceCounty;
      this.correspondenceAddress.PostCode =
        this.questionMap.CorrespondencePostCode;

      this.applicant2TelNo = this.questionMap.Applicant2TelNo;
      this.applicant2EmailAddress = this.questionMap.Applicant2EmailAddress;
      this.mortgageLender = this.questionMap.MortgageLender;
      this.mortgageStartDate = this.questionMap.MortgageStartDate;
      this.solicitorName = this.questionMap.SolicitorName;

      // solicitor address
      this.solicitorAddress.Street = this.questionMap.SolicitorAddressStreet;
      this.solicitorAddress.District =
        this.questionMap.SolicitorAddressDistrict;
      this.solicitorAddress.Town = this.questionMap.SolicitorAddressTown;
      this.solicitorAddress.County = this.questionMap.SolicitorAddressCounty;
      this.solicitorAddress.PostCode =
        this.questionMap.SolicitorAddressPostCode;

      this.emailAcceptToSolicitor = this.questionMap.EmailAcceptToSolicitor;
      this.emailNotificationToSolicitor =
        this.questionMap.EmailNotificationToSolicitor;
      this.yourRef = this.questionMap.YourRef;
      this.additionalInfo = this.questionMap.AdditionalInfo;
      this.solicitorEmailAddress = this.questionMap.SolicitorEmailAddress;

      // when retrieving a quote convert "Boolean Strings" to Booleans
      this.emailAcceptToSolicitor.Value = convertToBool(
        this.emailAcceptToSolicitor.Value
      );
      this.emailNotificationToSolicitor.Value = convertToBool(
        this.emailNotificationToSolicitor.Value
      );
    }

    this.limitedInformationStatement =
      this.questionMap.LimitedInformationStatement;

    // clear second applicant contact details
    this.resetSecondApplicantContactDetails();
  },
  mounted() {
    scrollTop();

    if (!this.restoringLastStep) {
      return;
    }

    setTimeout(async () => {
      tryGoToNextStep(
        this.$refs.yourApplicationForm,
        async (form) => await form.validate(),
        ROUTE_NAME.LANDLORDS.B2B.PAYMENT
      );
    }, 0);
  },
  methods: {
    ...mapActions(QUESTIONS_MODULE, ["toggleGlobalSaveAndExitDialog"]),

    correspondenceAddressSelected(response) {
      const item = response.data.Items[0];
      const propertiesAddress = formatAddress(item);
      this.correspondenceAddress.Street.Value = propertiesAddress.street;
      this.correspondenceAddress.PostCode.Value = item.PostalCode;
      this.correspondenceAddress.District.Value = propertiesAddress.district;
      this.correspondenceAddress.Town.Value = item.City;
      this.correspondenceAddress.County.Value = item.Province;

      // remove duplicate address values
      this.correspondenceAddress.County.Value =
        this.correspondenceAddress.Town.Value ===
        this.correspondenceAddress.County.Value
          ? ""
          : this.correspondenceAddress.County.Value;

      this.correspondenceAddress.District.Value =
        this.correspondenceAddress.Street.Value ===
        this.correspondenceAddress.District.Value
          ? ""
          : this.correspondenceAddress.District.Value;
    },
    correspondenceAddressLabel(val) {
      this.isShowCorrespondenceAddressLabel = val;
    },
    correspondenceUseAddressFinder() {
      this.correspondenceAddress.Street.Value = "";
      this.correspondenceAddress.District.Value = "";
      this.correspondenceAddress.Town.Value = "";
      this.correspondenceAddress.County.Value = "";
      this.correspondenceAddress.PostCode.Value = "";
    },
    solicitorAddressSelected(response) {
      const item = response.data.Items[0];
      const propertiesAddress = formatAddress(item);

      this.solicitorAddress.Street.Value = propertiesAddress.street;
      this.solicitorAddress.PostCode.Value = item.PostalCode;
      this.solicitorAddress.District.Value = propertiesAddress.district;
      this.solicitorAddress.Town.Value = item.City;
      this.solicitorAddress.County.Value = item.Province;

      // remove duplicate address values
      this.solicitorAddress.County.Value =
        this.solicitorAddress.Town.Value === this.solicitorAddress.County.Value
          ? ""
          : this.solicitorAddress.County.Value;

      this.solicitorAddress.District.Value =
        this.solicitorAddress.Street.Value ===
        this.solicitorAddress.District.Value
          ? ""
          : this.solicitorAddress.District.Value;
    },
    solicitorUseAddressFinder() {
      this.solicitorAddress.Street.Value = "";
      this.solicitorAddress.District.Value = "";
      this.solicitorAddress.Town.Value = "";
      this.solicitorAddress.County.Value = "";
      this.solicitorAddress.PostCode.Value = "";
    },
    solicitorAddressLabel(val) {
      this.isShowSolicitorAddressLabel = val;
    },
    async onSubmit(validate) {
      const isFormValid = await validate();
      if (!isFormValid) {
        const el = this.$el.querySelector(".field-invalid:first-of-type");
        this.$vuetify.goTo(el, { offset: 50 });
        return;
      }

      this.$router.push({ name: ROUTE_NAME.LANDLORDS.B2B.PAYMENT });
    },
    resetSecondApplicantContactDetails() {
      if (!this.isSecondApplicantIncluded) {
        this.applicant2TelNo.Value = "";
        this.applicant2EmailAddress.Value = "";
      }
    },
    setInitialDocDeliveryPref() {
      if (!this.documentDeliveryPreference.Value) {
        this.documentDeliveryPreference.Value = DOC_DELIVERY_PREF.Online; // default to Online
      } else if (
        this.documentDeliveryPreference.Value === DOC_DELIVERY_PREF.Postal
      ) {
        this.documentDeliveryPreferenceLocal = false; // make the UI match Postal
      }
    },
  },
};
</script>

<style lang="scss" scoped></style>
